import React, { useEffect, useReducer, useState } from "react";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import {
  Button,
  ButtonGroup,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Menu,
  MenuItem,
  Box
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useStyles } from "./GroupCampaignsStyles";
import useGroupCampaigns from "./useGroupCampaigns";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RepeatIcon from "@material-ui/icons/Repeat";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import StopIcon from "@material-ui/icons/Stop";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import GroupCampaignModal from "../../components/GroupCampaignModal";
import GroupCampaignExecutionsModal from "../../components/GroupCampaignExecutionsModal";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import { useTimezone } from "../../hooks/useTimezone";
import api from "../../services/api";



const GroupCampaigns = () => {
  const classes = useStyles();
  const theme = useTheme();
  const {
    addMenuAnchorEl,
    campaignModalOpen,
    campaignToPause,
    campaignToStop,
    campaigns,
    confirmModalOpen,
    confirmPauseCampaign,
    confirmStopCampaign,
    datetimeToClient,
    deletingCampaign,
    dispatch,
    executionsModalOpen,
    fetchCampaigns,
    formatDateTime,
    formatGroupScheduledDate,
    getConfiguredTimezone,
    handleCloseAddMenu,
    handleCloseCampaignModal,
    handleCopyCampaign,
    handleDeleteCampaign,
    handleEditCampaign,
    handleOpenAddMenu,
    handleOpenCampaignModal,
    handlePauseCampaign,
    handleSearch,
    handleStopCampaign,
    loading,
    page,
    pauseModalOpen,
    restartCampaign,
    rowsPerPage,
    searchParam,
    selectedCampaign,
    selectedCampaignForExecutions,
    selectedCampaignType,
    setAddMenuAnchorEl,
    setCampaignModalOpen,
    setCampaignToPause,
    setCampaignToStop,
    setConfirmModalOpen,
    setDeletingCampaign,
    setExecutionsModalOpen,
    setLoading,
    setPage,
    setPauseModalOpen,
    setRowsPerPage,
    setSearchParam,
    setSelectedCampaign,
    setSelectedCampaignForExecutions,
    setSelectedCampaignType,
    setStopModalOpen,
    stopModalOpen,
  } = useGroupCampaigns();

  const getStatusChip = (status) => {
    const statusMap = {
      scheduled: { label: "Programada", className: classes.statusScheduled },
      processing: { label: "En Proceso", className: classes.statusProcessing },
      completed: { label: "Completada", className: classes.statusCompleted },
      failed: { label: "Fallida", className: classes.statusFailed },
      cancelled: { label: "Cancelada", className: classes.statusCancelled },
    };

    const statusInfo = statusMap[status] || statusMap.scheduled;

    return (
      <Chip
        label={statusInfo.label}
        size="small"
        className={`${classes.statusChip} ${statusInfo.className}`}
      />
    );
  };

  const filteredCampaigns = campaigns.filter((campaign) => {
    // ✅ Validar que campaign y campaign.name existan antes de filtrar
    if (!campaign) {
      return false;
    }
    // Si no hay searchParam, mostrar todas las campañas (incluso sin name)
    if (!searchParam || searchParam.trim() === '') {
      return true;
    }
    // Si hay searchParam, validar que tenga name para buscar
    if (!campaign.name) {
      return false;
    }
    return campaign.name.toLowerCase().includes(searchParam);
  });

  return (
    <MainContainer>
      <MainHeader>
        <Title>Campañas de Grupos WA</Title>
      </MainHeader>

      <Paper className={classes.mainPaper}>
        <div className={classes.header}>
          <TextField
            placeholder="Buscar campañas..."
            type="search"
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={handleSearch}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.primary.main }} />
                </InputAdornment>
              ),
            }}
            style={{ flex: 1, marginRight: 16 }}
          />

          <ButtonGroup 
            variant="contained" 
            color="primary"
            style={{
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
              overflow: 'visible',
              position: 'relative',
              zIndex: 1
            }}
          >
            <Button
              onClick={() => handleOpenCampaignModal('UNICA')}
              style={{ 
                textTransform: 'uppercase', 
                fontWeight: '600',
                fontSize: '14px',
                padding: '10px 20px',
                letterSpacing: '0.5px'
              }}
              startIcon={<AddIcon />}
            >
              Nueva Campaña
            </Button>
            <Button
              size="small"
              onClick={handleOpenAddMenu}
              style={{ 
                minWidth: '36px', 
                padding: '0 8px',
                borderLeft: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Menu
            anchorEl={addMenuAnchorEl}
            open={Boolean(addMenuAnchorEl)}
            onClose={handleCloseAddMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            style={{
              zIndex: 1301
            }}
            PaperProps={{
              style: {
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                minWidth: '250px',
                marginTop: '4px',
                zIndex: 1301
              }
            }}
            MenuListProps={{
              style: {
                padding: '4px 0'
              }
            }}
            getContentAnchorEl={null}
          >
            <MenuItem 
              onClick={() => {
                handleOpenCampaignModal('UNICA');
                handleCloseAddMenu();
              }}
              style={{
                padding: '12px 20px',
                fontSize: '14px'
              }}
            >
              <Box display="flex" alignItems="center" width="100%">
                <span role="img" aria-label="Única" style={{ marginRight: 12, fontSize: '20px' }}>📱</span>
                Campaña ÚNICA
              </Box>
            </MenuItem>
            <MenuItem 
              onClick={() => {
                handleOpenCampaignModal('RECURRENTE');
                handleCloseAddMenu();
              }}
              style={{
                padding: '12px 20px',
                fontSize: '14px'
              }}
            >
              <Box display="flex" alignItems="center" width="100%">
                <span role="img" aria-label="Recurrente" style={{ marginRight: 12, fontSize: '20px' }}>🔄</span>
                Campaña RECURRENTE
              </Box>
            </MenuItem>
          </Menu>
        </div>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>Nombre</TableCell>
              <TableCell className={classes.tableHeader}>Estado</TableCell>
              <TableCell className={classes.tableHeader}>Tipo</TableCell>
              <TableCell className={classes.tableHeader}>Grupos</TableCell>
              <TableCell className={classes.tableHeader}>Ejecuciones</TableCell>
              <TableCell className={classes.tableHeader}>Próxima Ejecución</TableCell>
              <TableCell className={classes.tableHeader}>Conexión</TableCell>
              <TableCell className={classes.tableHeader}>Programada</TableCell>
              <TableCell className={classes.tableHeader}>Completada</TableCell>
              <TableCell className={classes.tableHeader}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              // ✅ PAGINACIÓN: Calcular campañas paginadas (igual que Campaigns)
              const paginatedCampaigns = filteredCampaigns.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              );
              
              if (loading) {
                return <TableRowSkeleton columns={10} />;
              }
              
              if (paginatedCampaigns.length > 0) {
                return paginatedCampaigns.map((campaign) => (
                <TableRow key={campaign.id} className={classes.tableRow}>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>{getStatusChip(campaign.status)}</TableCell>
                  <TableCell>
                    {/* ✅ REGLA DE NEGOCIO: Solo usar campaign.tipo - No puede haber UNICA con recurrencia */}
                    {campaign.tipo === 'GRUPO_RECURRENTE' ? (
                      <Chip
                        icon={<RepeatIcon />}
                        label="Recurrente"
                        color="primary"
                        size="small"
                        style={{ fontWeight: 'bold' }}
                      />
                    ) : (
                      <Chip
                        label="Única"
                        color="default"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {/* ✅ MEJORA UX: Mostrar contador X/Y con chips (igual que Campaigns) */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      {/* Contador X/Y - Siempre visible si hay datos, o mostrar 0/0 si no hay */}
                      {(() => {
                        const sentCount = campaign.sentGroups || 0;
                        const totalCount = campaign.totalGroups || 0;
                        
                        return (
                          <Chip
                            label={`${sentCount}/${totalCount}`}
                            size="small"
                            onClick={() => {
                              setSelectedCampaignForExecutions(campaign);
                              setExecutionsModalOpen(true);
                            }}
                            style={{ 
                              backgroundColor: sentCount === totalCount && totalCount > 0 
                                ? '#4caf50' 
                                : sentCount > 0 
                                  ? '#ff9800' 
                                  : '#e3f2fd',
                              color: sentCount === totalCount && totalCount > 0 
                                ? 'white' 
                                : sentCount > 0 
                                  ? 'white' 
                                  : '#1976d2',
                              fontWeight: 'bold',
                              minWidth: '50px',
                              cursor: 'pointer',
                            }}
                            title="Click para ver detalle de envíos"
                          />
                        );
                      })()}
                    </div>
                  </TableCell>
                  <TableCell>
                    {/* ✅ REGLA DE NEGOCIO: Solo usar campaign.tipo - No puede haber UNICA con recurrencia */}
                    {campaign.tipo === 'GRUPO_RECURRENTE' && campaign.executionCount !== undefined && campaign.executionCount !== null ? (
                      <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                        {campaign.executionCount}
                      </span>
                    ) : (
                      <span style={{ color: '#9e9e9e' }}>-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* ✅ REGLA DE NEGOCIO: Solo usar campaign.tipo - No puede haber UNICA con recurrencia */}
                    {campaign.tipo === 'GRUPO_RECURRENTE' && campaign.nextScheduledAt ? (
                      <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                        {formatDateTime(campaign.nextScheduledAt)}
                      </span>
                    ) : (
                      <span style={{ color: '#9e9e9e' }}>-</span>
                    )}
                  </TableCell>
                  <TableCell>{campaign.whatsapp?.name || "-"}</TableCell>
                  <TableCell>
                    {campaign.scheduledDate
                      ? formatGroupScheduledDate(campaign.scheduledDate)
                      : "-"}
                  </TableCell>
                  <TableCell align="center">
                    {/* ✅ CORRECCIÓN: Mostrar solo completedAt (fecha de completado), no lastExecutedAt */}
                    {campaign.completedAt
                      ? formatDateTime(campaign.completedAt)
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {/* ✅ Botón Pausar - solo para campañas en proceso */}
                    {campaign.status === "processing" && (
                      <IconButton
                        onClick={() => handlePauseCampaign(campaign)}
                        title="Pausar Campaña"
                        size="small"
                        className={classes.pauseIcon}
                      >
                        <PauseCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                    {/* ✅ Botón Parar - para campañas programadas o en proceso */}
                    {(campaign.status === "scheduled" || campaign.status === "processing") && (
                      <IconButton
                        onClick={() => handleStopCampaign(campaign)}
                        title="Parar Campaña Definitivamente"
                        size="small"
                        className={classes.stopIcon}
                      >
                        <StopIcon fontSize="small" />
                      </IconButton>
                    )}
                    {/* ✅ Botón Reanudar - para campañas canceladas con nextScheduledAt */}
                    {campaign.status === "cancelled" && campaign.nextScheduledAt && (
                      <IconButton
                        onClick={() => restartCampaign(campaign)}
                        title="Reanudar Campaña"
                        size="small"
                        className={classes.playIcon}
                      >
                        <PlayCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                    {/* ✅ Botón Copiar - solo para campañas completadas */}
                    {campaign.status === "completed" && (
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCampaign(campaign)}
                        className={classes.actionIcon}
                        title="Copiar campaña"
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleEditCampaign(campaign)}
                      className={classes.actionIcon}
                      title="Editar campaña"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeletingCampaign(campaign.id);
                        setConfirmModalOpen(true);
                      }}
                      className={classes.deleteIcon}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                ));
              }
              
              return (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    "No se encontraron campañas"
                  </TableCell>
                </TableRow>
              );
            })()}
          </TableBody>
        </Table>
        
        {/* ✅ PAGINACIÓN: Solo aparece cuando hay resultados (igual que Campaigns) */}
        {!loading && filteredCampaigns.length > 0 && (
          <TablePagination
            component="div"
            count={filteredCampaigns.length}
            page={page}
            onPageChange={(event, newPage) => {
              setPage(newPage);
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 13, 20, 50, 100]}
            labelRowsPerPage="Registros por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            style={{
              backgroundColor: 'transparent',
              color: 'inherit',
            }}
          />
        )}
      </Paper>

      <ConfirmationModal
        title="Eliminar Campaña"
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingCampaign(null);
        }}
        onConfirm={() => {
          if (deletingCampaign) {
            handleDeleteCampaign(deletingCampaign);
          }
          setConfirmModalOpen(false);
        }}
      >
        ¿Está seguro que desea eliminar esta campaña?
      </ConfirmationModal>

      {/* Modal de confirmación para Pausar */}
      <Dialog
        open={pauseModalOpen}
        onClose={() => {
          setPauseModalOpen(false);
          setCampaignToPause(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Pausar Campaña</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            ¿Está seguro de pausar esta campaña? Los mensajes ya enviados no se revierten. Podrá reanudarla después.
          </Typography>
          {campaignToPause && (
            <Typography variant="body2" style={{ marginTop: 16, fontWeight: 'bold' }}>
              Campaña: {campaignToPause.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPauseModalOpen(false);
              setCampaignToPause(null);
            }}
            color="default"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmPauseCampaign}
            color="primary"
            variant="contained"
          >
            Pausar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación para Parar */}
      <Dialog
        open={stopModalOpen}
        onClose={() => {
          setStopModalOpen(false);
          setCampaignToStop(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ color: "#d32f2f" }}>
          <span role="img" aria-label="Advertencia">⚠️</span> Parar Campaña Definitivamente
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" style={{ fontWeight: 'bold', color: "#d32f2f", marginBottom: 16 }}>
            <span role="img" aria-label="Advertencia">⚠️</span> ACCIÓN IRREVERSIBLE: ¿Está seguro de PARAR DEFINITIVAMENTE esta campaña?
          </Typography>
          <Typography variant="body2">
            Esta acción NO se puede deshacer. Los mensajes ya enviados se mantendrán, pero NO podrá reanudar la campaña.
          </Typography>
          {campaignToStop && (
            <Typography variant="body2" style={{ marginTop: 16, fontWeight: 'bold' }}>
              Campaña: {campaignToStop.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStopModalOpen(false);
              setCampaignToStop(null);
            }}
            color="default"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmStopCampaign}
            color="secondary"
            variant="contained"
          >
            Parar Definitivamente
          </Button>
        </DialogActions>
      </Dialog>

      <GroupCampaignModal
        open={campaignModalOpen}
        onClose={handleCloseCampaignModal}
        campaignId={selectedCampaign?.id}
        initialValues={selectedCampaign}
        campaignType={selectedCampaignType || (selectedCampaign && (selectedCampaign.tipo || (selectedCampaign.isRecurring ? 'RECURRENTE' : 'UNICA'))) || 'UNICA'}
        onSave={() => {
          fetchCampaigns();
        }}
      />

      {/* Modal de Ejecuciones */}
      {selectedCampaignForExecutions && (
        <GroupCampaignExecutionsModal
          open={executionsModalOpen}
          onClose={() => {
            setExecutionsModalOpen(false);
            setSelectedCampaignForExecutions(null);
          }}
          campaignId={selectedCampaignForExecutions.id}
          campaignName={selectedCampaignForExecutions.name}
        />
      )}
    </MainContainer>
  );
};

export default GroupCampaigns;

